/**
 * Option F — Post Stream Review Undo/Redo transform + authoritative position
 * writeback.
 *
 * The problem this solves: aggregate Undo/Redo used to store `[startPos,endPos]`
 * and remap them through the undo, but that remap is LOSSY for contiguous
 * same-anchor changes (a run of `add`s undone together collapses to one point and
 * cannot be re-separated on redo). Option F never reconstructs positions from a
 * collapsed value. It:
 *
 *   1. processes the touched segments FORWARD, in document `order`;
 *   2. applies each segment's target slice at the right place: a segment that still
 *      holds content is replaced at its own mapped range; a segment whose current
 *      slice is empty (an undone `add`, or an applied `remove`) is a zero-width
 *      insertion point, anchored by `order` within its run (`groupId`) relative to
 *      the already-present siblings — never its own collapsed/inflated stored range,
 *      which for a run of undone adds all points at the same anchor; and
 *   3. records each resulting range from the ACTUAL net document-size change
 *      (`endPos = to + Δsize`), robust to open / smart / rework slices.
 *
 * It is agnostic to the `slices[] + appliedIndex` history: `targetIndexFor`
 * returns the `appliedIndex` each segment should end at, so the same helper drives
 * step-through Undo/Redo (one segment steps by one) and aggregate Undo/Redo (every
 * non-accepted segment to a policy target — e.g. revert-all → 0, reapply-all →
 * latest).
 */
import { Slice } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIContentPositionEntry } from '../../../../pm-plugins/ai-session-context/types';
import { appliedSliceOf } from './segmentSlices';

/** `{ index, appliedIndex }` — set the raw-array entry's applied slice. */
export type AppliedIndexUpdateEntry = { appliedIndex: number; index: number };

/** Authoritative post-transform range for a touched entry. */
export type UndoRedoPositionEntry = { endPos: number; index: number; startPos: number };

export interface UndoRedoPlan {
	/**
	 * Pre-transform min/max range of the touched segments, to anchor the modal.
	 * These are in the current-document coordinate system; the decoration plugin
	 * maps them through `beforeTransform` (do NOT pass post-transform positions).
	 */
	anchor: { endPos: number; startPos: number };
	/** `appliedIndex` writeback for exactly the touched entries (raw indices). */
	appliedIndexUpdates: AppliedIndexUpdateEntry[];
	/** Replays the recorded ops on the command's transaction. */
	beforeTransform: (tr: Transaction) => Transaction;
	/** Authoritative post-transform ranges for the touched entries (raw indices). */
	positions: UndoRedoPositionEntry[];
}

/**
 * Build an Undo/Redo plan for the given segments.
 *
 * @param view The editor view (read-only here — the returned `beforeTransform`
 *   runs later on the command's transaction).
 * @param segments The full `streamReviewSegments` array (raw indices are used for
 *   the writebacks so they line up with the reducer).
 * @param targetIndexFor Returns the `appliedIndex` a segment should end at. A
 *   segment is skipped when it is accepted, already at its target, the target is
 *   out of range, or its stored range is out of the document.
 * @returns The plan, or `null` when nothing would change.
 */
export const computeUndoRedo = (
	view: EditorView,
	segments: readonly AIContentPositionEntry[],
	targetIndexFor: (seg: AIContentPositionEntry, index: number) => number,
): UndoRedoPlan | null => {
	const docSize = view.state.doc.content.size;

	const targets = segments
		.map((seg, index) => ({ seg, index }))
		.filter(({ seg, index }) => {
			const target = targetIndexFor(seg, index);
			if (
				seg.isAccepted ||
				target === seg.appliedIndex ||
				target < 0 ||
				target >= seg.slices.length
			) {
				return false;
			}
			// A present (non-empty) current slice is replaced in place, so its stored
			// range must be valid. An absent current slice is anchored by `order`, so
			// its (unreliable) stored range is neither used nor bounds-checked.
			if (appliedSliceOf(seg).content.size > 0) {
				return seg.startPos >= 0 && seg.endPos >= seg.startPos && seg.endPos <= docSize;
			}
			return true;
		})
		// FORWARD document order. `order` was stamped while ranges were distinct;
		// fall back to `startPos` for legacy entries without it.
		.sort((a, b) => (a.seg.order ?? a.seg.startPos) - (b.seg.order ?? b.seg.startPos));

	if (targets.length === 0) {
		return null;
	}

	// Simulate on a throwaway transaction to compute the ops + authoritative
	// ranges. `beforeTransform` replays the SAME (from,to,slice) ops on the
	// command's transaction, so the recorded positions are exact.
	const sim = view.state.tr;
	const ops: Array<{ from: number; slice: Slice; to: number }> = [];
	const positions: UndoRedoPositionEntry[] = [];
	const appliedIndexUpdates: AppliedIndexUpdateEntry[] = [];
	// Segments applied so far in this walk: raw index -> live range + whether it now
	// holds content. Consulted (alongside not-yet-touched siblings) when anchoring an
	// absent segment by order, so same-run siblings applied earlier in this walk count.
	const walk = new Map<number, { endPos: number; present: boolean; startPos: number }>();

	// Live range + presence of a sibling, accounting for this walk's inserts.
	const liveOf = (otherIndex: number): { endPos: number; present: boolean; startPos: number } => {
		const applied = walk.get(otherIndex);
		if (applied) {
			return applied;
		}
		const other = segments[otherIndex];
		if (appliedSliceOf(other).content.size === 0) {
			return { endPos: 0, present: false, startPos: 0 };
		}
		const a = sim.mapping.map(other.startPos, 1);
		const b = sim.mapping.map(other.endPos, -1);
		return { endPos: Math.max(a, b), present: true, startPos: Math.min(a, b) };
	};

	// Insertion anchor for an ABSENT segment (empty current slice): right after the
	// furthest-along present sibling in the SAME run (`groupId`) with a smaller
	// `order`; otherwise the segment's own mapped anchor (which places it before any
	// later content already at that anchor). Re-materialises a collapsed run in order
	// without trusting the segment's own collapsed/inflated stored range.
	const anchorForAbsent = (seg: AIContentPositionEntry, selfIndex: number): number => {
		const selfOrder = seg.order ?? seg.startPos;
		let anchor: number | null = null;
		if (seg.groupId != null) {
			for (let i = 0; i < segments.length; i++) {
				const other = segments[i];
				if (i === selfIndex || other.groupId !== seg.groupId) {
					continue;
				}
				if ((other.order ?? other.startPos) >= selfOrder) {
					continue;
				}
				const lr = liveOf(i);
				if (lr.present && (anchor === null || lr.endPos > anchor)) {
					anchor = lr.endPos;
				}
			}
		}
		return anchor ?? sim.mapping.map(seg.startPos, 1);
	};

	for (const { seg, index } of targets) {
		const target = targetIndexFor(seg, index);
		const slice = seg.slices[target] ?? Slice.empty;
		let from: number;
		let to: number;
		if (appliedSliceOf(seg).content.size > 0) {
			// Real content in the doc — replace its own mapped range.
			from = sim.mapping.map(seg.startPos, 1);
			to = Math.max(from, sim.mapping.map(seg.endPos, -1));
		} else {
			// Absent (undone `add` / applied `remove`): a zero-width insertion point.
			from = to = anchorForAbsent(seg, index);
		}
		const before = sim.doc.content.size;
		try {
			sim.replaceRange(from, to, slice);
		} catch {
			// Skip a segment whose range is no longer valid; do not writeback for it.
			continue;
		}
		const endPos = to + (sim.doc.content.size - before);
		ops.push({ from, to, slice });
		positions.push({ index, startPos: from, endPos });
		appliedIndexUpdates.push({ index, appliedIndex: target });
		walk.set(index, { endPos, present: slice.content.size > 0, startPos: from });
	}

	if (ops.length === 0) {
		return null;
	}

	// Modal anchor, in PRE-transform coordinates (the decoration plugin maps it
	// through `beforeTransform`). For a single op — step-through, or an aggregate
	// toggle that touched one segment — use the op's own range: that is where the
	// content actually lands, whereas the segment's stored range may be collapsed or
	// inflated for an undone `add`. For multiple ops, span the touched segments'
	// stored ranges (for a fully-collapsed run they share one anchor, which maps to
	// the whole re-inserted block).
	const anchor =
		ops.length === 1
			? { startPos: ops[0].from, endPos: ops[0].to }
			: {
					startPos: Math.min(...targets.map(({ seg }) => seg.startPos)),
					endPos: Math.max(...targets.map(({ seg }) => seg.endPos)),
				};

	const beforeTransform = (tr: Transaction): Transaction => {
		for (const op of ops) {
			try {
				tr.replaceRange(op.from, op.to, op.slice);
			} catch {
				// A concurrent edit invalidated the op — skip it (its writeback is
				// harmless: the generic remap still moves the untouched entry).
			}
		}
		return tr;
	};

	return { anchor, appliedIndexUpdates, beforeTransform, positions };
};
